<template>
    <div class="ps-home">
        <SeoHead home />

        <!-- Hero -->
        <section class="ps-home-hero relative overflow-hidden pt-28 pb-0 md:pt-32 lg:pt-36">
            <!-- Atmosphere -->
            <div class="ps-home-hero__aurora" aria-hidden="true">
                <div class="ps-home-hero__blob ps-home-hero__blob--1" />
                <div class="ps-home-hero__blob ps-home-hero__blob--2" />
            </div>
            <div class="ps-home-hero__grid" aria-hidden="true" />
            <div class="ps-home-hero__noise" aria-hidden="true" />

            <div class="container ps-home-hero__inner relative z-10 pb-20 md:pb-28 lg:pb-32">
                <div class="grid lg:grid-cols-[1fr_1.05fr] gap-14 lg:gap-12 xl:gap-16 items-center">
                    <!-- Copy -->
                    <div class="text-center lg:text-left order-2 lg:order-1">
                        <p class="ps-hero-eyebrow">
                            <span class="ps-hero-eyebrow__pulse">
                                <span class="ps-hero-eyebrow__pulse-dot" />
                                Live
                            </span>
                            Print cards + digital presence suite
                        </p>

                        <h1 class="ps-hero-title">
                            Build cards, pages &amp; leads
                            <span class="ps-hero-title__gradient">from one studio</span>
                        </h1>

                        <p class="ps-hero-lead mx-auto lg:mx-0">
                            <strong>ProfileSuite</strong> unites CardGen print wizard with vCards, link-in-bio, landing pages, resumes, and lead capture — self-hosted on Laravel.
                        </p>

                        <div class="ps-hero-tools-row justify-center lg:justify-start">
                            <Link
                                v-for="pill in heroToolPills"
                                :key="pill.slug"
                                :href="route('tools.show', pill.slug)"
                                class="ps-hero-tool-pill"
                            >
                                <icon :name="pill.icon" class="ps-hero-icon w-3.5 h-3.5" />
                                {{ pill.label }}
                            </Link>
                        </div>

                        <div class="ps-hero-actions justify-center lg:justify-start">
                            <Link
                                :href="route('tools.show', 'print-studio')"
                                class="ps-hero-cta-primary w-full sm:w-auto"
                            >
                                <icon name="print" class="ps-hero-icon w-5 h-5 relative z-10" />
                                <span class="relative z-10">Open Print Studio</span>
                            </Link>
                            <Link
                                :href="route('tools.show', 'business-card')"
                                class="ps-hero-cta-secondary w-full sm:w-auto"
                            >
                                <span>Explore digital tools</span>
                                <icon name="cheveron-right" class="ps-hero-icon w-4 h-4" />
                            </Link>
                        </div>

                        <div class="ps-hero-trust justify-center lg:justify-start">
                            <span v-for="item in heroTrust" :key="item.text" class="ps-hero-trust__item">
                                <span class="ps-hero-trust__icon">
                                    <icon :name="item.icon" class="ps-hero-icon w-3 h-3" />
                                </span>
                                {{ item.text }}
                            </span>
                        </div>
                    </div>

                    <!-- Visual -->
                    <div class="order-1 lg:order-2">
                        <home-hero-visual />
                    </div>
                </div>
            </div>
        </section>

        <!-- Stats strip -->
        <section class="ps-home-stats" aria-label="Platform highlights">
            <div class="container">
                <dl class="ps-home-stats__grid">
                    <div v-for="(stat, index) in stats" :key="stat.label" class="ps-home-stats__item">
                        <dt class="ps-home-stats__value">{{ stat.value }}</dt>
                        <dd class="ps-home-stats__label">{{ stat.label }}</dd>
                        <span v-if="index < stats.length - 1" class="ps-home-stats__divider" aria-hidden="true" />
                    </div>
                </dl>
            </div>
        </section>

        <!-- Product architecture -->
        <section class="ps-home-section ps-home-zones" aria-labelledby="zones-heading">
            <div class="container">
                <header class="ps-home-section__header ps-home-section__header--center">
                    <p class="ps-home-eyebrow">Product architecture</p>
                    <h2 id="zones-heading" class="ps-home-section__title">
                        Three zones, one product
                    </h2>
                    <p class="ps-home-section__lead">
                        Marketing site, tools workspace, and admin dashboard — structured for clarity from first visit to daily use.
                    </p>
                </header>

                <div class="ps-home-zones__flow" aria-hidden="true">
                    <span class="ps-home-zones__flow-node">Visit</span>
                    <span class="ps-home-zones__flow-line" />
                    <span class="ps-home-zones__flow-node">Create</span>
                    <span class="ps-home-zones__flow-line" />
                    <span class="ps-home-zones__flow-node">Manage</span>
                </div>

                <div class="ps-home-zones__grid">
                    <article
                        v-for="(zone, index) in zones"
                        :key="zone.title"
                        class="ps-home-zone-card"
                        :class="`ps-home-zone-card--${zone.theme}`"
                    >
                        <span class="ps-home-zone-card__index">0{{ index + 1 }}</span>
                        <div class="ps-home-zone-card__icon" :class="`ps-home-zone-card__icon--${zone.theme}`">
                            <icon :name="zone.icon" class="ps-home-icon w-6 h-6" />
                        </div>
                        <h3 class="ps-home-zone-card__title">{{ zone.title }}</h3>
                        <p class="ps-home-zone-card__desc">{{ zone.description }}</p>
                        <ul class="ps-home-zone-card__list">
                            <li v-for="point in zone.points" :key="point">
                                <icon name="tick" class="ps-home-icon w-4 h-4" />
                                {{ point }}
                            </li>
                        </ul>
                        <Link
                            v-if="zone.slug"
                            :href="route('tools.show', zone.slug)"
                            class="ps-home-zone-card__link"
                        >
                            {{ zone.cta }}
                            <icon name="cheveron-right" class="ps-home-icon w-3.5 h-3.5" />
                        </Link>
                    </article>
                </div>
            </div>
        </section>

        <!-- Tools bento -->
        <section class="ps-home-section ps-home-tools" aria-labelledby="tools-heading">
            <div class="container">
                <header class="ps-home-section__header ps-home-section__header--split">
                    <div>
                        <p class="ps-home-eyebrow">Creator toolkit</p>
                        <h2 id="tools-heading" class="ps-home-section__title">
                            Every tool you need
                        </h2>
                        <p class="ps-home-section__lead ps-home-section__lead--narrow">
                            From print layouts to lead forms — pick a tool and start creating. No plugins required.
                        </p>
                    </div>
                    <Link :href="route('tools.show', 'print-studio')" class="ps-home-link-arrow">
                        Explore all tools
                        <icon name="cheveron-right" class="ps-home-icon w-4 h-4" />
                    </Link>
                </header>

                <div class="ps-home-tools__bento">
                    <Link
                        v-for="tool in tools"
                        :key="tool.slug"
                        :href="route('tools.show', tool.slug)"
                        class="ps-home-tool-card"
                        :class="{
                            'ps-home-tool-card--featured': tool.featured,
                            'ps-home-tool-card--wide': tool.wide,
                        }"
                    >
                        <div class="ps-home-tool-card__top">
                            <div class="ps-home-tool-card__icon">
                                <icon :name="tool.icon" class="ps-home-icon w-5 h-5" />
                            </div>
                            <span
                                v-if="tool.badge"
                                class="ps-home-tool-card__badge"
                                :class="`ps-home-tool-card__badge--${tool.badgeTheme || 'default'}`"
                            >
                                {{ tool.badge }}
                            </span>
                        </div>
                        <h3 class="ps-home-tool-card__name">{{ tool.name }}</h3>
                        <p class="ps-home-tool-card__desc">{{ tool.description }}</p>
                        <span class="ps-home-tool-card__action">
                            Open tool
                            <icon name="cheveron-right" class="ps-home-icon w-3.5 h-3.5" />
                        </span>
                    </Link>
                </div>
            </div>
        </section>

        <!-- How it works -->
        <section class="ps-home-section ps-home-steps" aria-labelledby="how-heading">
            <div class="container">
                <header class="ps-home-section__header ps-home-section__header--center">
                    <p class="ps-home-eyebrow">Print Studio</p>
                    <h2 id="how-heading" class="ps-home-section__title">
                        Four steps to print-ready cards
                    </h2>
                    <p class="ps-home-section__lead">
                        The classic CardGen wizard — pick a template, fill details, preview, then print or export.
                    </p>
                </header>

                <ol class="ps-home-steps__track">
                    <li v-for="(step, index) in steps" :key="step.title" class="ps-home-step">
                        <div class="ps-home-step__marker">
                            <span class="ps-home-step__num">{{ index + 1 }}</span>
                            <span v-if="index < steps.length - 1" class="ps-home-step__line" aria-hidden="true" />
                        </div>
                        <div class="ps-home-step__body">
                            <h3 class="ps-home-step__title">{{ step.title }}</h3>
                            <p class="ps-home-step__desc">{{ step.description }}</p>
                        </div>
                    </li>
                </ol>

                <div class="ps-home-steps__cta">
                    <Link :href="route('tools.show', 'print-studio')" class="ps-home-btn ps-home-btn--dark">
                        Start designing cards
                        <icon name="cheveron-right" class="ps-home-icon w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>

        <!-- Benefits -->
        <section class="ps-home-section ps-home-benefits" aria-labelledby="benefits-heading">
            <div class="container">
                <div class="ps-home-benefits__grid">
                    <div class="ps-home-benefits__copy">
                        <p class="ps-home-eyebrow">Why ProfileSuite</p>
                        <h2 id="benefits-heading" class="ps-home-section__title">
                            Built for agencies, freelancers &amp; teams
                        </h2>
                        <p class="ps-home-section__lead">
                            Own your stack. Deploy on your server, brand the experience, and offer clients print cards plus a complete digital presence toolkit.
                        </p>

                        <ul class="ps-home-benefits__list">
                            <li v-for="benefit in benefits" :key="benefit.title" class="ps-home-benefit">
                                <div class="ps-home-benefit__icon">
                                    <icon :name="benefit.icon" class="ps-home-icon w-5 h-5" />
                                </div>
                                <div>
                                    <h3 class="ps-home-benefit__title">{{ benefit.title }}</h3>
                                    <p class="ps-home-benefit__desc">{{ benefit.description }}</p>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <aside class="ps-home-included">
                        <div class="ps-home-included__glow" aria-hidden="true" />
                        <p class="ps-home-included__label">What's included</p>
                        <p class="ps-home-included__hint">Everything in the suite — no add-on marketplace.</p>
                        <ul class="ps-home-included__grid">
                            <li v-for="item in included" :key="item" class="ps-home-included__item">
                                <icon name="tick" class="ps-home-icon w-4 h-4" />
                                {{ item }}
                            </li>
                        </ul>
                        <Link :href="route('tools.show', 'print-studio')" class="ps-home-included__cta">
                            Launch the studio
                            <icon name="cheveron-right" class="ps-home-icon w-4 h-4" />
                        </Link>
                    </aside>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="ps-home-cta" aria-labelledby="cta-heading">
            <div class="ps-home-cta__mesh" aria-hidden="true" />
            <div class="ps-home-cta__grid" aria-hidden="true" />
            <div class="container ps-home-cta__inner">
                <p class="ps-home-eyebrow ps-home-eyebrow--light">Get started</p>
                <h2 id="cta-heading" class="ps-home-cta__title">
                    Ready to build your next card or page?
                </h2>
                <p class="ps-home-cta__lead">
                    Jump into Print Studio for classic layouts, or open any digital tool from the sidebar — no account required to explore.
                </p>
                <div class="ps-home-cta__actions">
                    <Link :href="route('tools.show', 'print-studio')" class="ps-home-btn ps-home-btn--primary">
                        <icon name="print" class="ps-home-icon w-4 h-4" />
                        Launch Print Studio
                    </Link>
                    <Link :href="route('contact')" class="ps-home-btn ps-home-btn--ghost">
                        Contact us
                    </Link>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Landing/Layout'
import SeoHead from '@/Shared/SeoHead'
import Icon from '@/Shared/Icon'
import HomeHeroVisual from '@/Shared/Landing/HomeHeroVisual'

export default {
    layout: Layout,
    components: {
        Link,
        SeoHead,
        Icon,
        HomeHeroVisual,
    },
    props: {
        footer: Object,
        title: String,
        page: Object,
    },
    data() {
        return {
            heroToolPills: [
                { slug: 'print-studio', label: 'Print', icon: 'print' },
                { slug: 'business-card', label: 'Digital Card', icon: 'card' },
                { slug: 'link-in-bio', label: 'Bio Link', icon: 'website' },
                { slug: 'lead-form', label: 'Leads', icon: 'contact' },
            ],
            heroTrust: [
                { text: '12+ creator tools', icon: 'tick' },
                { text: 'Self-hosted Laravel', icon: 'security' },
                { text: 'No subscription lock-in', icon: 'check' },
            ],
            stats: [
                { value: '12+', label: 'Tools in one suite' },
                { value: '4-step', label: 'Print Studio wizard' },
                { value: '100%', label: 'Self-hosted control' },
                { value: 'Laravel', label: 'Modern PHP stack' },
            ],
            zones: [
                {
                    title: 'Print Studio',
                    description: 'Classic CardGen wizard for professional print-ready business cards.',
                    icon: 'print',
                    theme: 'emerald',
                    points: ['Portrait & landscape templates', 'QR codes & custom fields', 'PDF-ready export'],
                    cta: 'Open Print Studio',
                    slug: 'print-studio',
                },
                {
                    title: 'Digital Cards',
                    description: 'Shareable cards, vCards, NFC layouts, and email signatures.',
                    icon: 'card',
                    theme: 'indigo',
                    points: ['Business & referral cards', 'vCard (.vcf) downloads', 'QR & NFC-ready outputs'],
                    cta: 'Create digital card',
                    slug: 'business-card',
                },
                {
                    title: 'Web & Leads',
                    description: 'Pages and forms that turn visitors into contacts.',
                    icon: 'page',
                    theme: 'violet',
                    points: ['Link-in-bio & landing pages', 'Resume / CV builder', 'Lead capture forms'],
                    cta: 'Build a landing page',
                    slug: 'landing-page',
                },
            ],
            tools: [
                { slug: 'print-studio', name: 'Print Studio', icon: 'print', featured: true, wide: true, badge: 'Flagship', badgeTheme: 'amber', description: 'Design print-ready business cards with the classic 4-step wizard.' },
                { slug: 'business-card', name: 'Business Card', icon: 'card', description: 'Shareable digital card with photo, links, and QR code.' },
                { slug: 'link-in-bio', name: 'Link-in-Bio', icon: 'website', badge: 'Popular', badgeTheme: 'emerald', description: 'Mobile-first page with all your important links.' },
                { slug: 'landing-page', name: 'Landing Page', icon: 'page', description: 'Conversion-focused pages without writing code.' },
                { slug: 'lead-form', name: 'Lead Capture', icon: 'contact', description: 'Smart forms with custom fields and submissions.' },
                { slug: 'resume', name: 'Resume / CV', icon: 'file', description: 'Online resume with sections and shareable URL.' },
                { slug: 'referral-card', name: 'Referral Card', icon: 'card_user', description: 'Let customers refer friends with branded referral cards.' },
                { slug: 'nfc-card', name: 'NFC Card', icon: 'phone', description: 'Tap-to-share NFC card layouts for modern networking.' },
                { slug: 'vcard', name: 'vCard Generator', icon: 'contact', description: 'Downloadable .vcf files for phones and CRM imports.' },
                { slug: 'email-signature', name: 'Email Signature', icon: 'email', description: 'Professional HTML signatures for Gmail and Outlook.' },
                { slug: 'qr-code', name: 'QR Code', icon: 'code', description: 'Custom QR codes for URLs, Wi-Fi, and campaigns.' },
                { slug: 'floating-buttons', name: 'Floating Buttons', icon: 'arrow-right', description: 'Sticky action buttons for chat, call, and social.' },
            ],
            steps: [
                { title: 'Choose template', description: 'Pick portrait or landscape layouts designed for crisp print output.' },
                { title: 'Enter details', description: 'Add contact info, logo, colors, and optional QR code content.' },
                { title: 'Preview & adjust', description: 'Review every field on a live card preview before finalizing.' },
                { title: 'Print or export', description: 'Download print-ready files or send to your preferred printer.' },
            ],
            benefits: [
                { icon: 'security', title: 'Self-hosted & yours', description: 'Deploy on your infrastructure — full data ownership, no SaaS lock-in.' },
                { icon: 'palette', title: 'White-label ready', description: 'Brand the experience for clients with your logo, domain, and settings.' },
                { icon: 'dashboard', title: 'Admin dashboard', description: 'Manage users, custom card templates, leads, and site content.' },
            ],
            included: [
                'Print Studio wizard',
                'Digital card tools',
                'Link-in-bio pages',
                'Landing page builder',
                'Lead capture forms',
                'Resume / CV pages',
                'QR code generator',
                'Multi-language support',
            ],
        }
    },
}
</script>
